import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  downloadModelMetadataTemplate,
  downloadModelMetadataTemplateCsv,
  parseModelMetadataCsv,
  validateModelMetadataJson,
} from '../model-metadata-template.util';

@Component({
  selector: 'lib-model-metadata-json-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './model-metadata-json-upload.component.html',
  styleUrl: '../../dataset/metadata-json/metadata-json-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelMetadataJsonUploadComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly dragActive = signal(false);
  readonly selectedFile = signal<File | null>(null);
  readonly parsedJson = signal<Record<string, unknown> | null>(null);
  readonly uploadError = signal<string | null>(null);
  readonly missingFields = signal<string[]>([]);
  readonly templateMenuOpen = signal(false);

  toggleTemplateMenu(event: Event): void {
    event.stopPropagation();
    this.templateMenuOpen.update((open) => !open);
    this.cdr.markForCheck();
  }

  @HostListener('document:click')
  closeTemplateMenu(): void {
    if (!this.templateMenuOpen()) return;
    this.templateMenuOpen.set(false);
    this.cdr.markForCheck();
  }

  downloadTemplate(format: 'json' | 'csv', event: Event): void {
    event.stopPropagation();
    this.templateMenuOpen.set(false);
    if (format === 'json') {
      downloadModelMetadataTemplate('model-metadata-template.json');
    } else {
      downloadModelMetadataTemplateCsv('model-metadata-template.csv');
    }
    this.cdr.markForCheck();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFile(file);
    }
    input.value = '';
  }

  triggerFileInput(): void {
    this.fileInputRef()?.nativeElement.click();
  }

  removeFile(): void {
    this.selectedFile.set(null);
    this.parsedJson.set(null);
    this.uploadError.set(null);
    this.missingFields.set([]);
  }

  hasValidUpload(): boolean {
    return !!this.selectedFile() && !!this.parsedJson();
  }

  getParsedJson(): Record<string, unknown> | null {
    return this.parsedJson();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  }

  private handleFile(file: File): void {
    this.uploadError.set(null);
    this.missingFields.set([]);
    this.selectedFile.set(null);
    this.parsedJson.set(null);

    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (ext !== '.json' && ext !== '.csv') {
      this.uploadError.set(
        'This file type is not supported. Please upload a JSON or CSV file downloaded from the template.',
      );
      this.cdr.markForCheck();
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError.set(
        'This file is too large. The maximum allowed size is 10 MB. Please upload a smaller file.',
      );
      this.cdr.markForCheck();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const content = reader.result as string;
        const jsonData =
          ext === '.csv'
            ? parseModelMetadataCsv(content)
            : (JSON.parse(content) as Record<string, unknown>);
        const validation = validateModelMetadataJson(jsonData);

        if (validation.error) {
          this.uploadError.set(validation.error);
          this.cdr.markForCheck();
          return;
        }

        if (!validation.valid) {
          this.uploadError.set(
            ext === '.csv'
              ? 'Your CSV file is incomplete. Please fill in the missing details below and upload again:'
              : 'Your JSON file is incomplete. Please fill in the missing details below and upload again:',
          );
          this.missingFields.set(validation.missingFields);
          this.cdr.markForCheck();
          return;
        }

        this.selectedFile.set(file);
        this.parsedJson.set(jsonData);
        this.cdr.markForCheck();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        this.uploadError.set(
          ext === '.csv'
            ? message ||
              'We could not read this CSV file. Download the template, complete all required fields, and try again.'
            : 'We could not read this JSON file. Check that it is valid and matches the downloaded template.',
        );
        this.cdr.markForCheck();
      }
    };
    reader.onerror = () => {
      this.uploadError.set('We could not read this file. Please try uploading it again.');
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
  }
}
